import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectFeatureFlags } from 'core/userContext';
import { BUTTON_SIZES, GhostButton } from 'ui/button';
import { InputSearch } from 'ui/inputs';
import { Menu, MenuItemOption } from 'ui/menu';
import { Overline } from 'ui/theme';
import { ALIGNMENT } from 'ui/menu/constants';
import { FilterMenuTriggerButton } from 'ui/indexPages/filters/FilterMenuTriggerButton';
import {
  EMAILS_SENT,
  SMS_SEGMENTS_SENT,
  SMS_SENT,
  SMS_SEGMENTS_RECEIVED,
  MMS_ATTACHMENTS_SENT,
  MMS_ATTACHMENTS_RECEIVED,
  PUSH_NOTIFICATIONS_SENT,
  SMS_BILLING_METRICS,
  PROJECT_NAME_COLUMN_VALUE,
  WEB_PUSH_NOTIFICATIONS_SENT,
  IN_APP_NOTIFICATIONS_SENT,
  WHATSAPP_SENT,
} from '../../constants';
import { mapMetricToOption, mapTextToOptionTitle } from '../../utils';
import { BillingDateRangePicker } from '../Index/BillingDateRangePicker';
import { useSimpleIndexContext } from '../SimpleIndexPage/contexts';
import { Wrapper, SmsTitle } from './PageActions.styled';

export const SMS_METRICS = [SMS_SENT, ...SMS_BILLING_METRICS];

export const MessagingPageActions = () => {
  const { ExpandedChannelMetrics, WhatsAppMessageChannelGA } = useSelector(selectFeatureFlags);

  const { query, setQuery, activeColumns, setActiveColumns } = useSimpleIndexContext();

  const defaultColumns = [
    PROJECT_NAME_COLUMN_VALUE,
    EMAILS_SENT,
    SMS_SEGMENTS_SENT,
    SMS_SEGMENTS_RECEIVED,
    MMS_ATTACHMENTS_SENT,
    MMS_ATTACHMENTS_RECEIVED,
    PUSH_NOTIFICATIONS_SENT,
    ...(ExpandedChannelMetrics ? [WEB_PUSH_NOTIFICATIONS_SENT, IN_APP_NOTIFICATIONS_SENT] : []),
    ...(WhatsAppMessageChannelGA ? [WHATSAPP_SENT] : []),
  ];

  useEffect(() => {
    setActiveColumns(defaultColumns);
  }, []);

  /** -1 because Project name column is not selectable in filter options */
  const selectedFilterCount = activeColumns.length - 1;

  const handleSelection = (val: string) => {
    const next = activeColumns.includes(val)
      ? activeColumns.filter((activeFilterOption) => activeFilterOption !== val)
      : [...activeColumns, val];
    setActiveColumns(next);
  };

  const areAllSmsMetricsSelected = SMS_METRICS.every((metric) => activeColumns.includes(metric));
  const toggleAllSms = () => {
    const next = areAllSmsMetricsSelected
      ? activeColumns.filter(
          (column) => column === PROJECT_NAME_COLUMN_VALUE || !SMS_METRICS.includes(column),
        )
      : [...new Set([...activeColumns, ...SMS_METRICS])];
    setActiveColumns(next);
  };

  const SMS_FILTER_SECTION_TITLE = {
    id: 'SMS',
    content: (
      <SmsTitle style={{ minWidth: 200 }}>
        {/** minWidth adds additional spacing to the select dropdown */}
        <Overline>SMS</Overline>
        <GhostButton size={BUTTON_SIZES.SMALL} onClick={toggleAllSms}>
          {areAllSmsMetricsSelected ? 'Select none' : 'Select all'}
        </GhostButton>
      </SmsTitle>
    ),
    isTitle: true,
  };

  const filterOptions: MenuItemOption[] = [
    mapTextToOptionTitle('Email'),
    mapMetricToOption(EMAILS_SENT),
    SMS_FILTER_SECTION_TITLE,
    ...SMS_METRICS.map(mapMetricToOption),
    mapTextToOptionTitle('Push'),
    mapMetricToOption(PUSH_NOTIFICATIONS_SENT),
    ...(ExpandedChannelMetrics
      ? [
          mapTextToOptionTitle('In-app'),
          mapMetricToOption(IN_APP_NOTIFICATIONS_SENT),
          mapTextToOptionTitle('Web push'),
          mapMetricToOption(WEB_PUSH_NOTIFICATIONS_SENT),
        ]
      : []),
    ...(WhatsAppMessageChannelGA
      ? [mapTextToOptionTitle('WhatsApp'), mapMetricToOption(WHATSAPP_SENT)]
      : []),
  ];

  return (
    <Wrapper>
      <BillingDateRangePicker />
      <Menu
        options={filterOptions}
        defaultSelectedOptionIds={activeColumns}
        handleSelectionChange={handleSelection}
        horizontalAlignment={ALIGNMENT.LEFT}
        isSelectMenu={true}
        hasFilter={false}
      >
        <FilterMenuTriggerButton triggerText="Columns" selectedFilterCount={selectedFilterCount} />
      </Menu>
      <InputSearch
        data-test="table-search-input"
        id="table-search-input"
        placeholder="Search..."
        value={query}
        onChange={setQuery}
        handleClear={() => {
          setQuery('');
        }}
      />
    </Wrapper>
  );
};
